/**
 * Cliëntweergave bij beeldbellen: tekent alles lokaal, bedient niets, geen portal-auth.
 * Over de lijn komen alleen instellingen en start/stop binnen; terug gaat technische status.
 */
import { WsClient, type WsConnectionStatus, type WsMessage } from "./ws-client";
import { EMDR_KINDEREN_ENABLED } from "../lib/feature-flags";
import type { ViewerReport } from "../lib/beeldbellen-status";

export type ViewerCallbacks = {
  onStatus: (status: WsConnectionStatus) => void;
  onPlayingChange: (playing: boolean) => void;
  onSudChange: (open: boolean) => void;
  /** De behandelaar heeft de sessie beëindigd. */
  onEnded: () => void;
};

let client: WsClient | null = null;

function applyPlay(msg: WsMessage, callbacks: ViewerCallbacks): void {
  if (typeof msg.playing !== "boolean") return;
  window.__EMDR_APPLY_REMOTE_PLAY__?.(msg.playing, { seed: msg.seed, phase: msg.phase });
  callbacks.onPlayingChange(window.__EMDR_GET_PLAYING__?.() ?? msg.playing);
}

function applySud(open: boolean, resume: boolean, callbacks: ViewerCallbacks): void {
  window.__EMDR_SET_SUD__?.(open, resume);
  callbacks.onSudChange(window.__EMDR_GET_SUD__?.() ?? open);
  callbacks.onPlayingChange(window.__EMDR_GET_PLAYING__?.() ?? false);
}

export function connectViewer(code: string, callbacks: ViewerCallbacks): void {
  client?.disconnect();
  client = new WsClient();
  client.onStatus((status) => callbacks.onStatus(status));

  client.on("sync", (msg) => {
    if (msg.data && typeof msg.data === "object") {
      window.__EMDR_APPLY_REMOTE_STATE__?.(msg.data);
    }
    if (typeof msg.sud === "boolean") applySud(msg.sud, false, callbacks);
    applyPlay(msg, callbacks);
  });

  client.on("state", (msg) => {
    if (!msg.data || typeof msg.data !== "object") return;
    window.__EMDR_APPLY_REMOTE_STATE__?.(msg.data);
    if (typeof msg.seed === "number" && window.__EMDR_GET_PLAYING__?.()) {
      window.__EMDR_RESYNC__?.(msg.seed);
    }
  });

  client.on("play", (msg) => applyPlay(msg, callbacks));

  client.on("sud", (msg) => {
    if (typeof msg.open !== "boolean") return;
    if (typeof msg.seed === "number") window.__EMDR_SET_NEXT_SEED__?.(msg.seed);
    applySud(msg.open, msg.resume === true, callbacks);
  });

  client.on("session-end", () => {
    window.__EMDR_SET_SUD__?.(false, false);
    window.__EMDR_APPLY_REMOTE_PLAY__?.(false);
    callbacks.onPlayingChange(false);
    callbacks.onSudChange(false);
    callbacks.onEnded();
    disconnectViewer();
  });

  client.connect(code, "viewer");
}

export function sendViewerReport(report: ViewerReport): void {
  client?.send({ type: "viewer-status", data: report });
}

export function disconnectViewer(): void {
  client?.disconnect();
  client = null;
}

export function bootstrapViewerEmdr(): Promise<void> {
  window.__EMDR_VIEWER_MODE__ = true;
  // Vergrendeld: toetsen en klikken bij de cliënt starten of stoppen niets.
  window.__EMDR_DESKTOP_LOCKED__ = true;
  window.__EMDR_IS_PAID__ = true;
  window.__EMDR_ON_PAYWALL__ = () => {};

  return import("./figures").then(() =>
    import("./workload").then(() =>
      (EMDR_KINDEREN_ENABLED ? import("./workload-kinderen") : Promise.resolve()).then(() =>
        (EMDR_KINDEREN_ENABLED ? import("./kinderen-werkgeheugen-mode") : Promise.resolve()).then(() =>
          import("./racebaan").then(() =>
            import("./danspas").then(() =>
              import("./experimental").then(() =>
                import("./emdr-core").then(({ runEmdrInit }) => runEmdrInit())
              )
            )
          )
        )
      )
    )
  );
}
